import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Breadcrumbs, Title, WrapperFlex } from '@unimed-fesp/components';

import { format, isBefore, isWithinInterval, subMonths } from 'date-fns';
import { toast } from 'react-toastify';
import history from '../../services/history';
import { Creators as ModalidadeActions } from '../../store/ducks/modalidade';
import { Creators as RelatorioActions } from '../../store/ducks/relatorio';
// #region Import Componentes
import FadeIn from '../../components/atoms/FadeIn';
import { ModalConfimarRelatorio } from '../../components/molecules/PartsModalRelatorioBeneficiario';

import modal from '../../context/TriggerModalContext';
// #endregion

import theme from '../../styles/theme';
import { Container } from './styles';

import { WarningToast } from '../../components/molecules/Toast';
import FormRelatorioBeneficiario from './FormRelatorioBeneficiario';

const RelatorioBeneficiarios = () => {
  const dispatch = useDispatch();

  const { usuario } = useSelector((state) => state.auth);
  const { response } = useSelector(
    (state) => state.usuario.getContratantesUsuario
  );
  const { postRelatorioBeneficiarioRequest } = useSelector(
    (state) => state.relatorio
  );

  const formInitialParams = useMemo(
    () => ({
      tipoConsulta: 1,
      contratante: usuario.codigoContratante,
      grupoContratante: '',
      consultaPorGrupoContratante: false,
      modalidade: '',
      termo: '',
      layout: 'Layout 1',
      extensao: 'xlsx',
      dataPeriodoDeReferenciaDe: '',
      dataPeriodoDeReferenciaAte: '',
      exclusaoProgramada: false,
    }),
    []
  );

  const [formParams, setFormParams] = useState(formInitialParams);

  const contranteParametrizada = response.contranteParametrizada
    ? response.contranteParametrizada.find(
        (contratante) =>
          contratante.codigoContratante.toString() === usuario.codigoContratante
      )
    : [];

  const handleData = () => {
    const params = {
      exportar_xlsx: formParams.extensao === 'xlsx',
      leiaute_versao1: formParams.layout === 'Layout 1',
      email_usuario_logado: usuario.login,
      nome_usuario_logado: usuario.nome,
      codigo_contratante_logada: usuario.codigoContratante,
      filtro_contratante: formParams.consultaPorGrupoContratante
        ? contranteParametrizada.nomeComplemento
        : usuario.codigoContratante,
      modalidade: formParams.modalidade,
      termo: formParams.termo,
      tipo_consulta_beneficiario: formParams.tipoConsulta,
      data_referencia_inicial: formParams.dataPeriodoDeReferenciaDe
        ? format(formParams.dataPeriodoDeReferenciaDe, 'MM-dd-yyyy 00:00:00')
        : null,
      data_referencia_final: formParams.dataPeriodoDeReferenciaAte
        ? format(formParams.dataPeriodoDeReferenciaAte, 'MM-dd-yyyy 00:00:00')
        : null,
      beneficiario_com_data_exclusao_programada: formParams.exclusaoProgramada,
    };

    dispatch(RelatorioActions.postRelatorioBeneficiarioRequest(params));
  };

  const handleNovoRelatorio = useCallback(() => {
    if (formParams.tipoConsulta === 2) {
      const { dataPeriodoDeReferenciaDe, dataPeriodoDeReferenciaAte } =
        formParams;

      if (!dataPeriodoDeReferenciaDe || !dataPeriodoDeReferenciaAte) {
        return toast.error(
          <WarningToast>Insira um período de referência!</WarningToast>
        );
      }

      const dataAtual = new Date();
      const dataTresMesesAntes = subMonths(dataAtual, 3);

      if (isBefore(dataPeriodoDeReferenciaDe, dataTresMesesAntes)) {
        return toast.error(
          <WarningToast>
            A data inicial não pode retroagir mais que três meses!
          </WarningToast>
        );
      }
    }

    modal.warning(
      <ModalConfimarRelatorio.BodyConfimarRelatorio />,
      () => {},
      ({ closeModal }) => (
        <ModalConfimarRelatorio.FooterConfimarRelatorio
          closeModal={closeModal}
          onClickOk={() => {
            handleData();
            closeModal();
          }}
        />
      )
    );
  }, [dispatch, formParams]);

  useEffect(() => {
    if (usuario.role === 'Adm') {
      return history.push('/novo/lista');
    }

    dispatch(
      ModalidadeActions.getModalidadesRequest({
        codigoContratante: usuario.codigoContratante,
        getInativos: true,
      })
    );
  }, [dispatch, usuario.codigoContratante]);

  useEffect(() => {
    const { isSuccess, messageError } = postRelatorioBeneficiarioRequest.data;

    if (isSuccess) {
      history.push('/novo/processamento-relatorio');
    }

    if (!isSuccess && messageError) {
      toast.error(<WarningToast>{messageError}</WarningToast>);
    }

    dispatch(
      RelatorioActions.postRelatorioBeneficiarioResponse(false, {
        idTask: null,
        isSuccess: false,
      })
    );
  }, [
    dispatch,
    postRelatorioBeneficiarioRequest.data.isSuccess,
    postRelatorioBeneficiarioRequest.data.messageError,
  ]);
  return (
    <Container>
      <FadeIn>
        <WrapperFlex justifyStart alignStart column>
          <WrapperFlex fullWidth justifySpaceBetween alignStart>
            <Title
              text="Solicitação de Relatório de Beneficiários"
              color={theme.titleGray}
              fontSize={38}
              marginBottom={8}
            />
          </WrapperFlex>
          <Breadcrumbs
            crumbs={[
              { path: 'novo' },
              {
                path: 'Solicitação de Relatório de Beneficiários',
                onNavigate: () => history.push('/novo/relatorio-beneficiario'),
              },
            ]}
          />
        </WrapperFlex>
        <WrapperFlex column>
          <FormRelatorioBeneficiario
            grupoContratante={contranteParametrizada}
            formParams={formParams}
            setFormParams={setFormParams}
            clearParams={() => setFormParams(formInitialParams)}
            handleNovoRelatorio={() => handleNovoRelatorio()}
          />
        </WrapperFlex>
      </FadeIn>
    </Container>
  );
};

export default RelatorioBeneficiarios;
